/**
 * QL-13 — quizzes authored inside a `quiz` flow component.
 *
 * A quiz is not its own entity. Everything about it — the questions, the
 * point-scored options, the pass bar, the attempt limit — lives in the
 * component's `config`, exactly like every other component setting, and the
 * designer sets it up in the same dialog they use to add the component.
 * The component IS the quiz: its id identifies the quiz for the runtime blob,
 * the conditional-rule source and the PDF result field.
 *
 * Deliberately NOT the same thing as `product_type_assessment`, which models
 * the same scored-question idea but is bound to a product type inside a
 * portfolio collection and carries a per-category verdict. A quiz stands on
 * its own and answers one question: did this client pass?
 *
 * Labels are plain strings, not `LocalizedString`. That is the ticket's "no
 * multi language" — and it is also what keeps the AI-translate affordance out
 * of the editor, since that comes bundled with `LocalizedInput`.
 *
 * The pass and attempt rules are NOT redefined here. `sumAssessmentScore`,
 * `isAssessmentPassing` and `getQuizAttemptAvailability` in
 * `./product-assessments` are already pure and label-agnostic, so both features
 * share one definition of "passed" and one attempt/cooldown policy. Forking
 * them would guarantee they drift.
 */

/** One point-scored answer option. */
export type QuizOption = {
  id: string;
  label: string;
  score: number;
};

export type QuizQuestion = {
  id: string;
  label: string;
  /** At least 2 options. */
  options: QuizOption[];
  /**
   * QL-253 — a generic, unnamed grouping slot (1-10), or null/absent for
   * "None". Once any question in the pool carries a real category,
   * `questionsToAsk` draws that many questions from EACH category present
   * (None included) instead of one flat draw across the whole pool — see
   * `drawQuestions` in the quiz runtime (`quiz-compute.ts`). Purely a
   * draw-composition control; never shown to the person taking the quiz.
   */
  category?: number | null;
};

/**
 * The authored half of a `quiz` component's config — everything the designer
 * sets up. Carries option scores, so it is server-side and designer-side only:
 * never hand this to a client, use `QuizPublic` / `QuizPublicQuestion`.
 */
export type QuizSettings = {
  questions: QuizQuestion[];
  /** Pass bar — the summed score of the drawn questions must be >= this. */
  minScore: number;
  /** Number of questions drawn at random per attempt. */
  questionsToAsk: number;
  /** Max attempts before cooldown. Null = unlimited. */
  maxAttempts: number | null;
  /** Days after the last attempt when the counter resets. Null = never. */
  attemptResetDays: number | null;
};

/** One question with option scores stripped (client-safe). */
export type QuizPublicQuestion = {
  id: string;
  label: string;
  options: Array<{ id: string; label: string }>;
};

/**
 * Client-safe projection of one quiz component. Scores are always stripped,
 * and the question set is omitted entirely — questions are drawn per attempt
 * via quiz/start, so shipping them up front would hand over the whole bank.
 *
 * `id` is the flow component's id, and `name` its configured title: the two
 * things a client-facing widget and the PDF field label need.
 */
export type QuizPublic = {
  id: string;
  name: string;
  description: string | null;
  minScore: number;
  /**
   * QL-253 — the configured per-category draw count, not the attempt total:
   * once any question carries a real category, an attempt draws this many
   * from EACH category present (None included). Read `questionsPerAttempt`
   * for the actual number of questions an attempt contains.
   */
  questionsToAsk: number;
  /** QL-253 — the real number of questions an attempt contains, computed from `questionsToAsk` and the current categories. */
  questionsPerAttempt: number;
  maxAttempts: number | null;
  attemptResetDays: number | null;
};

/** Default title for a quiz component whose `title` the designer left blank. */
export const UNTITLED_QUIZ_NAME = "Quiz";

/**
 * What a picker needs to offer one quiz as a choice: the conditional-rule
 * source lists, the PDF placeholder menus, the flow editor. Shared because the
 * API returns it and the web hooks consume it verbatim.
 *
 * `id` is the flow component's id — the quiz id in rules, in the runtime blob
 * and in the PDF result field.
 */
export type FlowQuizSummary = {
  id: string;
  name: string;
  questionCount: number;
  minScore: number;
  questionsToAsk: number;
};

// --- Public API: a flow's quiz banks (QL-680) ---
// `GET /api/v1/flows/{id}/quizzes` and `…/quizzes/{quizId}` — the authored bank
// behind every `quiz` component of a flow, for an integrator that maps questions
// and options ahead of time and joins QL-528's attempts back to them. Carries
// option scores, so it is an answer key: API-key surface ONLY, never the
// client's flow session (that keeps using `QuizPublic` / `QuizPublicQuestion`).

/** One option with its score — the same `id` as `answers[].selectedOptionId` in QL-528's attempts. */
export type FlowQuizBankOption = {
  id: string;
  label: string;
  score: number;
};

/** One question of the bank — the same `id` `quiz/start` deals and `attempts[].questionIds` lists. */
export type FlowQuizBankQuestion = {
  id: string;
  label: string;
  /** QL-253 draw group (1-10), null for "None". Never shown to the client. */
  category: number | null;
  /** The best score any option gives — the same definition as `answerScores[].maxScore` (QL-528). Null when the question has no options. */
  maxScore: number | null;
  options: FlowQuizBankOption[];
};

/** One quiz of the flow without its questions: what `GET /flows/{id}/quizzes` lists. */
export type FlowQuizBankSummary = {
  /** The flow component's id — the `quizId` of quiz/start, quiz/submit and QL-528's attempts/answerScores. */
  quizId: string;
  name: string;
  description: string | null;
  /** Questions in the bank. */
  questionCount: number;
  /** Configured per-category draw count (see `QuizPublic.questionsToAsk`). */
  questionsToAsk: number;
  /** Questions an attempt actually contains. */
  questionsPerAttempt: number;
  minScore: number;
  maxAttempts: number | null;
  attemptResetDays: number | null;
};

/** The summary plus the full bank: `GET /flows/{id}/quizzes/{quizId}`. */
export type FlowQuizBank = FlowQuizBankSummary & {
  questions: FlowQuizBankQuestion[];
};

/** Empty when the flow has no set-up quiz component; the quiz endpoints then do not apply. */
export type FlowQuizBanksResponse = {
  data: FlowQuizBankSummary[];
};

// --- Result status ---

/**
 * The three values the PDF field `<Quiz Name> Result` can take, and the same
 * three the completion engine reasons about.
 */
export type QuizResultStatus = "unanswered" | "passed" | "failed";

/**
 * Narrows a raw stitched field value. The PDF field map is
 * `Record<string, string>`, so the renderer has no compile-time guarantee that
 * `quiz_result_<id>` holds one of the three — a stale snapshot written before
 * a status was renamed would otherwise be cast straight into a label lookup
 * and print `undefined` on a compliance document.
 */
export function isQuizResultStatus(value: string): value is QuizResultStatus {
  return value === "unanswered" || value === "passed" || value === "failed";
}

/**
 * Deterministic status from the stored record alone — no clock. The PDF and
 * the completion engine both use this, so a rendered document cannot change
 * its verdict between reads the way a cooldown-aware view would.
 *
 * "failed" means the client has used at least one attempt and has not passed.
 * Whether they may try again is an availability question, not a status one.
 *
 * An advisor override counts as passed. It is deliberately not a fourth
 * status: the ticket specifies exactly three PDF values, and a reader of the
 * document wants to know the gate was cleared. `QuizOutcome.override` carries
 * the who/why/when for anyone who needs to audit *how* it was cleared.
 */
export function classifyQuizResultStatus(
  state: { attemptsCount: number; passed: boolean; override?: unknown | null } | null | undefined,
): QuizResultStatus {
  if (!state) return "unanswered";
  if (state.override) return "passed";
  if (state.attemptsCount === 0) return "unanswered";
  return state.passed ? "passed" : "failed";
}

/**
 * Tells repeated quiz titles apart, in the one order every reader agrees on:
 * page then component. Two quiz components can carry the same title, and every
 * untitled one falls back to `UNTITLED_QUIZ_NAME`, so a bare title is not an
 * identifier. The rule and placeholder menus offer these names, the PDF editor
 * round-trips `{{Quiz.<name>}}` through them, and the override dialog names the
 * quiz it is about to clear — all of which need the repeat to be visible.
 *
 * Returns a stateful namer; call it once per quiz in order.
 */
export function makeUniqueQuizNamer(): (name: string) => string {
  // Every name handed out, not a per-title counter: a designer may well have
  // typed "Quiz (2)" themselves, and counting alone would hand that same
  // string to the second "Quiz" — two quizzes, one `{{Quiz.<name>}}`.
  const used = new Set<string>();
  return (name: string): string => {
    let candidate = name;
    for (let suffix = 2; used.has(candidate.toLowerCase()); suffix++) {
      candidate = `${name} (${suffix})`;
    }
    used.add(candidate.toLowerCase());
    return candidate;
  };
}

/**
 * Read one quiz's status out of a `quiz` component's `calculatedData` blob.
 * A quiz the client has never touched has no entry — that is "unanswered",
 * not "missing", so every conditional evaluator can treat the two alike.
 */
export function readQuizResultStatus(
  calculatedData: string | null | undefined,
  quizId: string,
): QuizResultStatus {
  if (!calculatedData || !quizId) return "unanswered";
  try {
    const parsed = JSON.parse(calculatedData) as {
      results?: Record<string, { status?: QuizResultStatus }>;
    };
    return parsed?.results?.[quizId]?.status ?? "unanswered";
  } catch {
    return "unanswered";
  }
}

/**
 * QL-361 — one quiz's score out of the same blob's `results` map: the last
 * completed attempt's score, in points. Null when there is none — never
 * attempted, an externally attested result, or an advisor override with no real
 * attempt behind it. Never synthesised from the status.
 *
 * Not a score-rule reader on its own: a group answered per member always reads
 * null here (its aggregate `results` entry carries no score). Score conditions
 * go through `readQuizScoreRange` in `assessment-condition-sources.ts`, which
 * reads `memberResults[]` for groups and falls back to this for single clients.
 */
export function readQuizScore(
  calculatedData: string | null | undefined,
  quizId: string,
): number | null {
  if (!calculatedData || !quizId) return null;
  try {
    const parsed = JSON.parse(calculatedData) as {
      results?: Record<string, { score?: number | null }>;
    };
    const score = parsed?.results?.[quizId]?.score;
    return typeof score === "number" && Number.isFinite(score) ? score : null;
  } catch {
    return null;
  }
}

/**
 * Validation shared by the runtime service and the component settings dialog,
 * so the form cannot offer a shape the server will reject.
 *
 * Returns an error key, or null when the quiz is savable.
 */
export function validateQuizConfig(config: QuizSettings): string | null {
  const { questions, minScore, questionsToAsk, maxAttempts, attemptResetDays } = config;

  if (questions.length === 0) return "quiz.noQuestions";
  for (const question of questions) {
    if (question.label.trim() === "") return "quiz.emptyQuestionLabel";
    if (question.options.length < 2) return "quiz.tooFewOptions";
    if (question.options.some((option) => option.label.trim() === "")) {
      return "quiz.emptyOptionLabel";
    }
    // QL-253 — a generic grouping slot; null/absent is "None".
    if (
      question.category != null &&
      (!Number.isInteger(question.category) || question.category < 1 || question.category > 10)
    ) {
      return "quiz.categoryRange";
    }
  }
  // Counts and limits are discrete — a `questionsToAsk` of 1.5 draws an
  // arbitrary number of questions and a fractional attempt limit is unreachable.
  //
  // QL-253 — no upper bound tied to the pool size: once categories are in
  // use, questionsToAsk draws that many PER CATEGORY, and a category smaller
  // than questionsToAsk already degrades gracefully in `drawQuestions`
  // (same graceful shortfall handling applies with no categories at all,
  // where every question forms one implicit group).
  if (!Number.isInteger(questionsToAsk) || questionsToAsk < 1) {
    return "quiz.questionsToAskRange";
  }
  if (minScore < 0) return "quiz.minScoreRange";
  if (maxAttempts != null && (!Number.isInteger(maxAttempts) || maxAttempts < 1)) {
    return "quiz.maxAttemptsRange";
  }
  if (attemptResetDays != null && (!Number.isInteger(attemptResetDays) || attemptResetDays < 1)) {
    return "quiz.attemptResetDaysRange";
  }

  // A pass bar no draw can reach makes the quiz unpassable. Check the best
  // possible draw per the category-aware draw in `drawQuestions`: group
  // questions by category (None included, keyed by `null`), and for each
  // group take the highest-scoring option of its highest-scoring
  // `questionsToAsk` questions (or all of them, if the group is smaller) —
  // then sum across every group. With every question in one group — every
  // config that predates QL-253, and any config that still doesn't use
  // categories — this is identical to the original flat calculation.
  const groups = new Map<number | null, QuizQuestion[]>();
  for (const question of questions) {
    const key = question.category ?? null;
    const group = groups.get(key);
    if (group) group.push(question);
    else groups.set(key, [question]);
  }
  let bestPossible = 0;
  for (const group of groups.values()) {
    const bestPerQuestion = group
      .map((question) => Math.max(...question.options.map((option) => option.score)))
      .sort((a, b) => b - a);
    bestPossible += bestPerQuestion
      .slice(0, Math.min(questionsToAsk, group.length))
      .reduce((sum, score) => sum + score, 0);
  }
  if (bestPossible < minScore) return "quiz.unpassable";

  return null;
}

/** A question object usable by the validator, the draw and the grader. */
function isWellFormedQuestion(question: unknown): question is QuizQuestion {
  if (question == null || typeof question !== "object") return false;
  const candidate = question as Partial<QuizQuestion>;
  if (typeof candidate.id !== "string" || candidate.id === "") return false;
  if (typeof candidate.label !== "string") return false;
  if (!Array.isArray(candidate.options)) return false;
  return candidate.options.every(
    (option) =>
      option != null &&
      typeof option === "object" &&
      typeof (option as QuizOption).id === "string" &&
      (option as QuizOption).id !== "" &&
      typeof (option as QuizOption).label === "string" &&
      typeof (option as QuizOption).score === "number" &&
      Number.isFinite((option as QuizOption).score),
  );
}

/**
 * Read the authored quiz out of a `quiz` component's raw `config` JSON.
 *
 * Returns null for a component that is not set up yet — no config, broken
 * JSON, or no questions. Every caller treats that the same way: a quiz with no
 * questions asks nothing, so it cannot gate anything and it contributes no
 * result field. Keeping that judgement in one function is what stops the
 * runtime, the completion engine and the PDF from disagreeing about whether a
 * half-built component counts.
 */
export function quizSettingsFromConfig(config: string | null | undefined): QuizSettings | null {
  if (!config) return null;
  let parsed: Partial<QuizSettings> | null;
  try {
    parsed = JSON.parse(config) as Partial<QuizSettings>;
  } catch {
    return null;
  }
  const raw = Array.isArray(parsed?.questions) ? parsed.questions : [];
  // A config written by hand or by an AI tool can be shaped wrong, not just
  // absent. Anything that is not a well-formed question is dropped here rather
  // than left to blow up in `validateQuizConfig`, `drawQuestions` or the
  // grader, each of which assumes `label` and `options` exist.
  const questions = raw.filter(isWellFormedQuestion);
  if (questions.length === 0) return null;
  return {
    questions,
    minScore: typeof parsed?.minScore === "number" ? parsed.minScore : 1,
    // A saved component always carries this; the fallback covers a config
    // hand-written by an AI tool that omitted it.
    questionsToAsk:
      typeof parsed?.questionsToAsk === "number" ? parsed.questionsToAsk : questions.length,
    maxAttempts: typeof parsed?.maxAttempts === "number" ? parsed.maxAttempts : null,
    attemptResetDays: typeof parsed?.attemptResetDays === "number" ? parsed.attemptResetDays : null,
  };
}

/** The quiz component's display name: its configured title, or a default. */
export function quizNameFromConfig(config: string | null | undefined): string {
  if (!config) return UNTITLED_QUIZ_NAME;
  try {
    const title = (JSON.parse(config) as { title?: unknown }).title;
    return typeof title === "string" && title.trim() !== "" ? title.trim() : UNTITLED_QUIZ_NAME;
  } catch {
    return UNTITLED_QUIZ_NAME;
  }
}

/** The component's optional prose blurb, shown above the quiz widget. Null when blank or absent. */
export function quizDescriptionFromConfig(config: string | null | undefined): string | null {
  if (!config) return null;
  try {
    const description = (JSON.parse(config) as { description?: unknown }).description;
    return typeof description === "string" && description.trim() !== "" ? description : null;
  } catch {
    return null;
  }
}
